/*
 * Copyright (c)  CYBERKOALA LLC 2025.
 * Proprietary License.
 * Redistribution and use in source and binary forms, with or without
 * modification, are restricted.
 */
import { Input } from '@heroui/react';
import { Link, NavLink } from 'react-router-dom';
import {
  FaGithub,
  FaLinkedin,
  FaOdnoklassniki,
  FaTelegram,
  FaVk,
  FaWhatsapp,
  FaYoutube,
} from 'react-icons/fa';
import { SocialLinks } from '@/social-links.ts';
import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, BugIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile.tsx';
import { useNavigate } from 'react-router';
import { ScrollArea } from '@/components/chartsdb/scroll-area/scroll-area.tsx';
import { isPardusBrowser } from '@/components/check-user-agent.tsx';
import { Button } from '@heroui/button';
import { ModalType, useModal } from '@/stores/use-modal-store.ts';
import { useAuth } from '@/context/auth-context/use-auth.ts';

type MeSettingsSidebarProps = {
  onItemClick?: () => void;
};

export const MeSettingsSidebar: React.FC<MeSettingsSidebarProps> = ({ onItemClick }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { onOpen } = useModal();
  const { user: sessionUser } = useAuth();

  const settingsSections = [
    {
      title: t('MeSettingsSidebar.user_settings'),
      items: [
        {
          name: t('MeSettingsSidebar.my_account'),
          path: '/channels/@me/account',
        },
        {
          name: t('MeSettingsSidebar.change_password'),
          path: '/channels/@me/change-password',
        },
        { name: t('MeSettingsSidebar.devices'), path: '/channels/@me/devices' },
      ],
    },
    {
      title: t('MeSettingsSidebar.payment_settings'),
      items: [
        {
          name: t('MeSettingsSidebar.subscriptions'),
          path: '/channels/@me/boost',
        },
      ],
    },
    {
      title: t('MeSettingsSidebar.app_settings'),
      items: [
        {
          name: t('MeSettingsSidebar.appearance'),
          path: '/channels/@me/appearance',
        },
        {
          name: t('MeSettingsSidebar.voice_n_video'),
          path: '/channels/@me/voice-video',
        },
        {
          name: t('MeSettingsSidebar.notifications'),
          path: '/channels/@me/notifications',
        },
        {
          name: t('MeSettingsSidebar.keybinds'),
          path: '/channels/@me/keybinds',
        },
        {
          name: t('MeSettingsSidebar.language'),
          path: '/channels/@me/language',
        },
        // {
        //   name: t('MeSettingsSidebar.advanced'),
        //   path: '/channels/@me/advanced',
        // },
        {
          name: t('MeSettingsSidebar.games_database'),
          path: '/channels/@me/gamesdb',
        },
      ],
    },
    {
      title: t('MeSettingsSidebar.activity_settings'),
      items: [
        {
          name: t('games_library.title'),
          path: '/channels/@me/games-library',
        },
        {
          name: t('MeSettingsSidebar.activity_privacy'),
          path: '/channels/@me/activity-privacy',
        },
      ],
    },
    // {
    //   title: t('MeSettingsSidebar.legal'),
    //   items: [
    //     { name: t('MeSettingsSidebar.privacy'), path: '/legal/privacy' },
    //     { name: t('MeSettingsSidebar.terms'), path: '/legal/terms' },
    //   ],
    // },
  ];

  const filteredSections = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    if (!query) return settingsSections;

    return settingsSections
      .map((section) => {
        const filteredItems = section.items.filter((item) =>
          item.name.toLowerCase().includes(query)
        );
        return filteredItems.length > 0 ? { ...section, items: filteredItems } : null;
      })
      .filter(Boolean) as typeof settingsSections;
  }, [searchQuery]);

  const onDeleteUserAction = (action: ModalType) => {
    onOpen(action, { userId: sessionUser?.id });
  };

  return (
    <aside className="overscroll-y-auto flex md:w-72 h-full flex-col border-r border-l border-foreground-100 bg-background">
      {isMobile && (
        <Link
          to={'/'}
          className="mt-2 flex flex-row items-center hover:text-stone-500"
          onClick={onItemClick}
        >
          <ArrowLeftIcon size={18} className="ml-2" />
          {t('MeSettingsSidebar.back_to_servers')}
        </Link>
      )}
      {isPardusBrowser() && <div className="mt-6"></div>}
      <div className="p-4">
        <Input
          placeholder={t('MeSettingsSidebar.search')}
          className="w-full focus:ring-0"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      <ScrollArea>
        <nav className="flex-1 p-2 space-y-4">
          {isPardusBrowser() && (
            <div className="flex flex-col gap-2">
              <ul className="space-y-1">
                <li>
                  <NavLink
                    to="/channels/@me/desktop"
                    className={({ isActive }) =>
                      `block px-3 py-1.5 rounded text-sm ${
                        isActive
                          ? 'bg-stone-700 text-white'
                          : 'text-foreground-500 hover:text-foreground-800 dark:hover:text-foreground-300'
                      }`
                    }
                  >
                    {t('MeSettingsSidebar.desktop_app_settings_title')}
                  </NavLink>
                </li>
              </ul>
            </div>
          )}
          {filteredSections.map((section, idx) => (
            <div key={idx}>
              <p className="text-xs uppercase mb-2 px-2 text-foreground-500">{section.title}</p>
              <ul className="space-y-1">
                {section.items.map((item) => (
                  <li key={item.path}>
                    {isMobile ? (
                      <button
                        onClick={() => {
                          navigate(item.path);
                          onItemClick?.();
                        }}
                        className="block w-full text-left px-3 py-1.5 rounded text-sm text-foreground-500 hover:text-foreground-800 dark:hover:text-foreground-300"
                      >
                        {item.name}
                      </button>
                    ) : (
                      <NavLink
                        to={item.path}
                        className={({ isActive }) =>
                          `block px-3 py-1.5 rounded text-sm ${
                            isActive
                              ? 'bg-stone-700 text-white'
                              : 'text-foreground-500 hover:text-foreground-800 dark:hover:text-foreground-300'
                          }`
                        }
                      >
                        {item.name}
                      </NavLink>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {filteredSections.length === 0 && (
            <p className="text-sm px-3">{t('MeSettingsSidebar.nothing_found')}</p>
          )}
          <Button
            type="button"
            variant="light"
            size="sm"
            className="text-muted-foreground hover:text-stone-500"
            onPress={() => onDeleteUserAction('deleteUser')}
          >
            <Trash2Icon className="mr-2 size-4 text-red-500" />
            <span>{t('Settings.deleteAccount')}</span>
          </Button>
        </nav>
      </ScrollArea>
      <div className="grid grid-cols-9 grid-rows-1 items-center justify-between border-t border-background-400 p-2 text-center text-sm text-foreground-300">
        {/*<a href={SocialLinks.telegram} target="_blank" rel="noopener noreferrer">*/}
        {/*  <FaTelegram size={16} />*/}
        {/*</a>*/}
        <a href={SocialLinks.max} target="_blank" rel="noopener noreferrer">
          <img
            src="/icons/max-messenger-sign-logo.svg"
            style={{ width: '16px', filter: 'grayscale(100%) opacity(0.5)' }}
            alt="Max messenger"
          />
        </a>
        <a href={SocialLinks.vk} target="_blank" rel="noopener noreferrer">
          <FaVk size={16} />
        </a>
        <a href={SocialLinks.youtube} target="_blank" rel="noopener noreferrer">
          <FaYoutube size={16} />
        </a>
        {/*<a href={SocialLinks.whatsapp} target="_blank" rel="noopener noreferrer">*/}
        {/*  <FaWhatsapp size={16} />*/}
        {/*</a>*/}
        {/*<a href={SocialLinks.linkedin} target="_blank" rel="noopener noreferrer">*/}
        {/*  <FaLinkedin size={16} />*/}
        {/*</a>*/}
        {/*<a href={SocialLinks.github} target="_blank" rel="noopener noreferrer">*/}
        {/*  <FaGithub size={16} />*/}
        {/*</a>*/}
        <a href={SocialLinks.ok} target="_blank" rel="noopener noreferrer">
          <FaOdnoklassniki size={16} />
        </a>
        <div className="p-2 space-y-4">
          <a
            href={'https://ccalls.site/support/contact.html'}
            target="_blank"
            rel="noopener noreferrer"
          >
            <BugIcon
              className="text-center text-sm text-foreground-400 hover:text-foreground-800"
              size={20}
            />
          </a>
        </div>
      </div>
      <a
        href="https://cyberkoalastudios.com"
        target="_blank"
        className="mt-auto cursor-pointer px-4 pb-2 text-xs transition text-foreground-300 hover:text-indigo-600 dark:hover:text-indigo-400"
        rel="noreferrer"
      >
        © {new Date().getFullYear()} CyberKoala
        <span className="align-super text-xs">
          <sup>&reg;</sup>
        </span>
      </a>
      <div className="mb-16" />
    </aside>
  );
};
